import i18next from 'i18next';
import Personalizer from 'personalizer';
import NumberFormats from 'utils/personalizer/NumberFormats';
import TimeUtil from 'utils/date/TimeUtil';
import { getExerciseTranslation, isBodyWeightExercise } from 'utils/ExerciseTypesUtil';

i18next.loadNamespaces(['activity', 'activity-api', 'units']);

// If an exercise has a higher probability than this, it will override a higher probability of an UNKNOWN exercise
export const UNKNOWN_PROBABILITY_OVERRIDE = 24.9999999999;

export const getExerciseCategoryAndType = (exercises, isReadOnly) => {
    const retVal = {
        categoryKey: '',
        exerciseKey: 'choose_an_exercise',
        localizeString: i18next.t('activity:choose_an_exercise')
    };
    let highestProb = 0;

    // non-owner viewing the activity
    if (isReadOnly) {
        retVal.localizeString = i18next.t('activity-api:device.unknown'); // Show unknown to non-owners when viewing a page where the exercise isn't set
    }

    if (exercises && exercises.length > 0) {
        exercises.forEach((exerciseCandidate) => {
            if (exerciseCandidate.probability > highestProb) {
                retVal.localizeString = getExerciseTranslation(exerciseCandidate);
                retVal.exerciseKey = exerciseCandidate.exerciseKey;
                retVal.categoryKey = exerciseCandidate.categoryKey;
                highestProb = exerciseCandidate.probability;
            }
        });
    }

    return retVal;
};

export const convertToCategoryAndExerciseKey = (exercise) => {
    // for each exercise set, transform the response to have categoryKey and exerciseKey
    // these fields are used throughout GCW instead of category & name
    if (!exercise.categoryKey || !exercise.exerciseKey) {
        exercise.categoryKey = exercise.category;
        exercise.exerciseKey = exercise.name || exercise.category; // if name is null, set category as exerciseKey
        delete exercise.name;
        delete exercise.category;
    }

    return exercise;
};

export const convertFromCategoryAndExerciseKey = (exercise) => {
    // for each exercise set, transform the payload to have the fields expected by the API:
    // category & name instead of categoryKey & exerciseKey
    exercise.category = exercise.categoryKey;
    exercise.name = exercise.exerciseKey !== exercise.categoryKey ? exercise.exerciseKey : null;
    delete exercise.categoryKey;
    delete exercise.exerciseKey;
    return exercise;
};

export const getLabels = (isPilatesActivity, isYogaActivity) => {
    let labels = {
        setNumberTitle: i18next.t('units:label_set'),
        setName: i18next.t('activity:label_exercise_name'),
        chooseSetLabelKey: null // will be replaced by the exerciseType
    };

    if (isPilatesActivity) {
        labels = {
            setNumberTitle: i18next.t('activity:label_move'),
            setName: i18next.t('activity:label_move_name'),
            chooseSetLabelKey: i18next.t('activity:choose_a_move')
        };
    }

    if (isYogaActivity) {
        labels = {
            setNumberTitle: i18next.t('activity:label_pose'),
            setName: i18next.t('activity:label_pose_name'),
            chooseSetLabelKey: i18next.t('activity:choose_a_pose')
        };
    }

    return labels;
};

export const getEditLabels = (isPilatesActivity, isYogaActivity) => {
    var labels = {
        setNameLabel: i18next.t('activity:label_exercise_name'),
        addSetLabel: i18next.t('activity:add_exercise')
    };

    if (isPilatesActivity) {
        labels = {
            setNameLabel: i18next.t('activity:label_move_name'),
            addSetLabel: i18next.t('activity:add_move')
        };
    }

    if (isYogaActivity) {
        labels = {
            setNameLabel: i18next.t('activity:label_pose_name'),
            addSetLabel: i18next.t('activity:add_pose')
        };
    }

    return labels;
};

/**
 * returns true when weight is not positive and not body weight eercise
 * @param {{ weight: number | undefined | null}} exerciseSet
 * @param {string} categoryKey
 * @param {string} exerciseKey
 * @param {any} flattenedExerciseTypes
 * @returns
 */
export const hasNoWeight = (exerciseSet, categoryKey, exerciseKey, flattenedExerciseTypes) => {
    const weightIsNotPositive =
        !exerciseSet ||
        (exerciseSet && (exerciseSet.weight === null || exerciseSet.weight === undefined || exerciseSet.weight === -1));
    return weightIsNotPositive && !isBodyWeightExercise(categoryKey, exerciseKey, flattenedExerciseTypes);
};

export const getExerciseWeight = (exerciseSet, categoryKey, exerciseKey, flattenedExerciseTypes) => {
    const weightIsNotPositive =
        !exerciseSet ||
        (exerciseSet && (exerciseSet.weight === null || exerciseSet.weight === undefined || exerciseSet.weight === -1));
    if (weightIsNotPositive && !isBodyWeightExercise(categoryKey, exerciseKey, flattenedExerciseTypes)) {
        return i18next.t('activity:add_weight');
    } else if (weightIsNotPositive) {
        return i18next.t('activity:body_weight');
    } else {
        const value = parseFloat(exerciseSet.weight);
        return Personalizer.personalizeWeight(value, NumberFormats.PRECISION1_NOSEP_NOZERO);
    }
};

export const shouldDisplayBodyweight = (weight = -1) => {
    return weight === -1 || weight === 0 || weight === '';
};

export const getExerciseByTime = (timeInHrMinSec, exerciseSetsByTime) => {
    if (!timeInHrMinSec || !exerciseSetsByTime) {
        return null;
    }

    const seconds = TimeUtil.hoursMinutesAndSecondsToSeconds(timeInHrMinSec);
    const exercise = exerciseSetsByTime.find(
        (ex) => ex.startTimeInSeconds <= seconds && seconds <= ex.endTimeInSeconds
    );

    if (!exercise) {
        return null;
    }

    // rest steps don't have categoryKey or exerciseKey
    if (!exercise.exerciseKey) {
        return i18next.t('activity:interval_type_rest');
    }

    if (exercise.exerciseKey === 'choose_an_exercise') {
        return i18next.t('activity-api:device.unknown');
    }

    return exercise.localizeString;
};
